import React, { useEffect, useState } from 'react'

import Button from '@react/react-spectrum/Button'
import Dialog from '@react/react-spectrum/Dialog'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import NumberInput from '@react/react-spectrum/NumberInput'
import Select from '@react/react-spectrum/Select'
import { Table, TBody, TD, TH, THead, TR } from '@react/react-spectrum/Table'
import Textfield from '@react/react-spectrum/Textfield'
import IconPicker from 'components/IconPicker'
import ScreenPicker from 'components/ScreenPicker'
import Delete from '@react/react-spectrum/Icon/Delete'
import Edit from '@react/react-spectrum/Icon/Edit'
import ArrowUp from '@react/react-spectrum/Icon/ArrowUp'
import ArrowDown from '@react/react-spectrum/Icon/ArrowDown'

import Tooltip from 'components/Tooltip'

import { arrayToObject, isObject, objectToArray } from 'utils/collections'

import './FormTable.scss'

/*
EXAMPLE of dynamically controlling input method for a different field in the same row

const DYNAMIC_COLUMN_TYPE = [
  { name: 'label', label: 'Property Name/Path', isKey: true },
  { name: 'valueType', label: 'Type', type: 'fieldType' },
  { name: 'value', label: 'Value', type: 'dynamic', typeColumn: 'valueType' },
  {
    name: 'data',
    label: 'Data',
    tooltip:
      'Additional properties to set in dataLayer for this option. Should follow JSON Object syntax: { "path1": " value1", "path2": "value2"}',
  },
]
*/

const DEFAULT_COLUMNS = [
  { name: 'label', label: 'Property Name/Path', isKey: true },
  { name: 'value', label: 'Value', type: 'dynamic', typeColumn: 'valueType' },
]

const FormTable = ({ columns, fields, disabled, saveAs, onChange, projectType }) => {
  const [rows, setRows] = useState([])
  const _columns = columns || DEFAULT_COLUMNS

  const keyProperty = _columns.find((c) => c.isKey)?.name
  const hasEmptyRow = keyProperty && rows.length > 0 && rows.filter((r) => !r[keyProperty]).length > 0

  const _onChange = (v) => (saveAs === 'object' ? onChange(arrayToObject(v)) : onChange(v))

  const saveField = (index, propName, isKey) => (value) => {
    let _value = isKey ? value.replace(/(^[0-9]+|-|\s)/gm, '') : value
    const newRows = rows.map((r, idx) => (idx === index ? { ...r, [propName]: _value } : r))
    setRows(newRows)
    _onChange(newRows)
  }

  const addRow = () => {
    if (!hasEmptyRow && !disabled) {
      const newRows = [...rows, { id: `row-${rows.length}-${Date.now()}` }]
      setRows(newRows)
    }
  }

  const deleteRow = (id) => {
    const newRows = rows.filter((r) => r.id !== id)
    setRows(newRows)
    _onChange(newRows)
  }

  const moveEntry = (id, direction) => {
    const index = rows.findIndex((row) => row.id === id)
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= rows.length) return

    const updated = [...rows]
    const [movedItem] = updated.splice(index, 1)
    updated.splice(newIndex, 0, movedItem)

    setRows(updated)
    _onChange(updated)
  }

  useEffect(() => {
    const assignIds = (arr) =>
      (arr || []).map((item, index) => ({
        id: item.id || `row-${index}-${Date.now()}`,
        ...item,
      }))

    if (isObject(fields)) {
      setRows(assignIds(objectToArray(fields)))
    } else {
      setRows(assignIds(fields))
    }
  }, [fields])

  return (
    <div className='formTable'>
      {Array.isArray(_columns) && (
        <Table onSubmit={(evt) => evt.preventDefault()}>
          <THead>
            {_columns.map((c) => (
              <TH key={c.name}>
                <div>
                  {c.label}
                  {c.tooltip && <Tooltip content={c.tooltip} footer={c.tooltipFooter} />}
                </div>
              </TH>
            ))}
            <TH></TH>
          </THead>
          <TBody>
            {rows.map((row, idx) => (
              <TR key={'field-' + idx}>
                {_columns.map((c) => (
                  <TD key={'field-' + idx + c.name}>
                    <Field
                      type={c.type === 'dynamic' ? row[c.typeColumn] : c.type}
                      projectType={projectType}
                      options={c.options}
                      allowExternal={c.allowExternal}
                      allowProduct={c.allowProduct}
                      value={row[c.name]}
                      disabled={disabled}
                      onChange={saveField(idx, c.name, c.isKey)}
                    />
                  </TD>
                ))}
                <TD>
                  <div className='row-actions'>
                    <Button
                      variant='tool'
                      aria-label='Move Up'
                      icon={<ArrowUp />}
                      onClick={() => moveEntry(row.id, -1)}
                      disabled={disabled || idx === 0}
                    />
                    <Button
                      variant='tool'
                      aria-label='Move Down'
                      icon={<ArrowDown />}
                      onClick={() => moveEntry(row.id, 1)}
                      disabled={disabled || idx === rows.length - 1}
                    />
                    <Button
                      variant='tool'
                      aria-label='Delete'
                      icon={<Delete />}
                      onClick={() => deleteRow(row.id)}
                      disabled={disabled}
                    />
                  </div>
                </TD>
              </TR>
            ))}
            {!disabled && (
              <TR onClick={addRow}>
                <TD>
                  <Button variant='action' quiet label='+ Add' disabled={disabled || hasEmptyRow} />
                </TD>
                {_columns.map((c) => (
                  <TD key={c.name} />
                ))}
              </TR>
            )}
          </TBody>
        </Table>
      )}
    </div>
  )
}

FormTable.defaultProps = {
  columns: DEFAULT_COLUMNS,
  saveAs: 'array',
  disabled: false,
}

const FormTableDialog = ({ label, columns, saveAs, fields, disabled, onChange, projectType, ...otherProps }) => {
  const [newContent, setNewContent] = useState(fields || [])
  return (
    <Dialog
      {...otherProps}
      className={'option-editor--dialog'}
      title={label || 'Option Editor'}
      confirmLabel='Save'
      cancelLabel='Discard'
      confirmDisabled={disabled}
      onConfirm={() => onChange(newContent)}
      role='dialog'
    >
      <FormTable
        columns={columns}
        saveAs={saveAs}
        fields={newContent}
        disabled={disabled}
        onChange={setNewContent}
        projectType={projectType}
      />
    </Dialog>
  )
}

const FormTableInput = ({ label, columns, saveAs, fields, disabled, onChange, projectType }) => (
  <ModalTrigger>
    <Button icon={<Edit />} disabled={disabled}>
      Open Editor
    </Button>
    <FormTableDialog
      label={label}
      columns={columns}
      saveAs={saveAs}
      fields={fields}
      onChange={onChange}
      disabled={disabled}
      projectType={projectType}
    />
  </ModalTrigger>
)

const Field = ({ type, value, options, disabled, onChange, projectType, allowExternal, allowProduct }) => {
  switch (type) {
    case 'screen':
      return (
        <ScreenPicker
          value={value}
          onChange={onChange}
          disabled={disabled}
          allowExternal={allowExternal}
          allowProduct={allowProduct}
        />
      )
    case 'integer':
      return <NumberInput value={value} disabled={disabled} onChange={(v) => onChange(parseInt(v))} />
    case 'double':
      return <NumberInput value={value} disabled={disabled} onChange={onChange} step={'any'} />
    case 'boolean':
      return (
        <Select
          options={[
            { label: 'True', value: 'true' },
            { label: 'False', value: 'false' },
          ]}
          value={(value === true || value === 'true').toString()}
          disabled={disabled}
          onChange={(v) => onChange(v === 'true')}
        />
      )
    case 'dropdown':
      return <Select options={options} value={value} disabled={disabled} onChange={onChange} />
    case 'icon':
      return (
        <IconPicker
          name={''}
          value={value || ''}
          onChange={onChange}
          disabled={disabled}
          iconSet={options}
          isMobileV2={projectType === 'mobile-v2'}
        />
      )
    case 'fieldType':
      return (
        <Select
          options={[
            { label: 'String', value: 'string' },
            { label: 'Integer', value: 'integer' },
            { label: 'Double', value: 'double' },
            { label: 'Boolean', value: 'boolean' },
          ]}
          value={value || 'string'}
          disabled={disabled}
          onChange={onChange}
        />
      )
    case 'string':
    default:
      return <Textfield value={value || ''} disabled={disabled} onChange={onChange} />
  }
}

export { FormTable, FormTableDialog, FormTableInput }
